/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import PillarTableColumnArray from "shared/components/pillar-table/columns/array/PillarTableColumnArray";
import PillarTableColumnBoolean from "shared/components/pillar-table/columns/PillarTableColumnBoolean";
import PillarTableColumnButton from "shared/components/pillar-table/columns/PillarTableColumnButton";
import PillarTableColumnCheckbox from "shared/components/pillar-table/columns/PillarTableColumnCheckbox";
import PillarTableColumnCreateModified from "shared/components/pillar-table/columns/PillarTableColumnCreateModified";
import PillarTableColumnDateTime from "shared/components/pillar-table/columns/PillarTableColumnDateTime";
import PillarTableColumnProfileAddress from "shared/components/pillar-table/columns/PillarTableColumnProfileAddress";
import PillarTableColumnProfileContactInformation from "shared/components/pillar-table/columns/PillarTableColumnProfileContactInformation";
import PillarTableColumnProfileDegree from "shared/components/pillar-table/columns/PillarTableColumnProfileDegree";
import PillarTableColumnProfileDisplayName from "shared/components/pillar-table/columns/PillarTableColumnProfileDisplayName";
import PillarTableColumnProfileEmail from "shared/components/pillar-table/columns/PillarTableColumnProfileEmail";
import PillarTableColumnProfileTag from "shared/components/pillar-table/columns/PillarTableColumnProfileTag";
import PillarTableColumnTemplate from "shared/components/pillar-table/columns/PillarTableColumnTemplate";
import PillarTableColumnText from "shared/components/pillar-table/columns/PillarTableColumnText";
import PillarTableColumnTextRegex from "shared/components/pillar-table/columns/PillarTableColumnTextRegex";
import PillarTableColumnUSD from "shared/components/pillar-table/columns/PillarTableColumnUSD";
import PillarTableColumnWorkflowInstancePossibleActions from "shared/components/pillar-table/columns/PillarTableColumnWorkflowInstancePossibleActions";
import PillarTableColumnWorkflowInstanceStatus from "shared/components/pillar-table/columns/PillarTableColumnWorkflowInstanceStatus";
import { type PillarDataTableFixedColumn } from "shared/components/pillar-table/helpers/addFixedStyleToColumn";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { type DeepKeys } from "shared/types/deepkeys";
export interface PillarTableEditColumnProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> extends PillarTableBaseColumnCellValuePathProps<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  > {
  formValuePath?: string;
}

export interface PillarTableBaseColumnProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> {
  adminOnly?: boolean;
  headingClassName?: string;
  heading?: string;
  linkToAdmin?: {
    path: string;
    params?: Record<string, string>;
  };
  linkToHub?: string;
  testid?: string;
  className?: string;
  orderByKey?: string;
  minWidth?: string;
  maxWidth?: string;
  fixedColumn?: PillarDataTableFixedColumn;
  contentClassName?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  ref?: React.Ref<HTMLDivElement>;
}

export type PillarTableBaseColumnCellValuePathProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = PillarTableBaseColumnProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> & {
  cellValuePath?: DeepKeys<RowGeneric>;
};

export type RenderChildren<T> = (
  cellValue: T,
  setCellValue: (value: any, valueProperty: string) => void,
) => React.ReactElement;

export interface PillarTableColumnProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> extends PillarTableBaseColumnProps<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  > {
  children?: ((row: RowGeneric) => React.ReactElement) | React.ReactElement;
  /**
   * @deprecated Use the correct column type directly instead of using this generic column.
   */
  valueProperty?: string;
  /*xw
   * @deprecated Use `linkToAdmin` or `linkToHub` instead.
   * This is used for legacy reasons, prefer using `linkToAdmin`.
   */
  linkTo?:
    | string
    | {
        path: string;
        params?: Record<string, string>;
      };
  /**
   * @deprecated This will not be necessary as the "formValuePath" will decide if it is editable or not.
   */
  doNotEdit?: boolean;

  /**
   * @deprecated Use `PillarTableColumnButton` or the linkToAdmin/hub properties instead
   */
  cancelRowClickAction?: boolean;
}

// Explicitly type the component with its static members to avoid TS7056 (overly large inferred type)
export interface PillarTableColumnComponent {
  <
    RowGeneric = PillarTableSafeAny,
    ExtraPropsGeneric = PillarTableSafeAny | undefined,
    InlineEditFormGeneric = PillarTableSafeAny | undefined,
  >(
    props: PillarTableColumnProps<
      RowGeneric,
      ExtraPropsGeneric,
      InlineEditFormGeneric
    >,
  ): React.ReactElement | null;
  Boolean: typeof PillarTableColumnBoolean;
  Button: typeof PillarTableColumnButton;
  Checkbox: typeof PillarTableColumnCheckbox;
  CreateModified: typeof PillarTableColumnCreateModified;
  DateTime: typeof PillarTableColumnDateTime;
  PossibleActions: typeof PillarTableColumnWorkflowInstancePossibleActions;
  ProfileAddress: typeof PillarTableColumnProfileAddress;
  ProfileContactInformation: typeof PillarTableColumnProfileContactInformation;
  ProfileDegree: typeof PillarTableColumnProfileDegree;
  ProfileDisplayName: typeof PillarTableColumnProfileDisplayName;
  ProfileEmail: typeof PillarTableColumnProfileEmail;
  ProfileTag: typeof PillarTableColumnProfileTag;
  Template: typeof PillarTableColumnTemplate;
  Text: typeof PillarTableColumnText;
  USD: typeof PillarTableColumnUSD;
  WorkflowInstanceStatus: typeof PillarTableColumnWorkflowInstanceStatus;
  WorkflowInstancePossibleActions: typeof PillarTableColumnWorkflowInstancePossibleActions;
  Array: typeof PillarTableColumnArray;
  TextRegex: typeof PillarTableColumnTextRegex;
}

const PillarTableColumn = (({ children }) => {
  return <>{children}</>;
}) as PillarTableColumnComponent;
PillarTableColumn.Boolean = PillarTableColumnBoolean;
PillarTableColumn.Button = PillarTableColumnButton;
PillarTableColumn.Checkbox = PillarTableColumnCheckbox;
PillarTableColumn.CreateModified = PillarTableColumnCreateModified;
PillarTableColumn.DateTime = PillarTableColumnDateTime;
PillarTableColumn.PossibleActions =
  PillarTableColumnWorkflowInstancePossibleActions;
PillarTableColumn.ProfileAddress = PillarTableColumnProfileAddress;
PillarTableColumn.ProfileContactInformation =
  PillarTableColumnProfileContactInformation;
PillarTableColumn.ProfileDegree = PillarTableColumnProfileDegree;
PillarTableColumn.ProfileDisplayName = PillarTableColumnProfileDisplayName;
PillarTableColumn.ProfileEmail = PillarTableColumnProfileEmail;
PillarTableColumn.ProfileTag = PillarTableColumnProfileTag;
PillarTableColumn.Template = PillarTableColumnTemplate;
PillarTableColumn.Text = PillarTableColumnText;
PillarTableColumn.USD = PillarTableColumnUSD;
PillarTableColumn.WorkflowInstanceStatus =
  PillarTableColumnWorkflowInstanceStatus;
PillarTableColumn.WorkflowInstancePossibleActions =
  PillarTableColumnWorkflowInstancePossibleActions;
PillarTableColumn.Button = PillarTableColumnButton;
PillarTableColumn.Array = PillarTableColumnArray;
PillarTableColumn.TextRegex = PillarTableColumnTextRegex;
export default PillarTableColumn;
